import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';
import { ExoticBet, StraightExoticRow } from '../../queries/predictedExotics';
import {
  formatOutlay,
  getCorrectCountDotStates,
  getMatchBadgeInfo,
  parseRaceId,
  straightCombinations,
} from './predicted-exotics-helpers';

function MatchBadge({ correctCount, target }: { correctCount: number; target: number }) {
  const { label, colourClass } = getMatchBadgeInfo(correctCount, target);
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold text-white ${colourClass}`}>
      {label}
    </span>
  );
}

function CorrectCountDots({ correctCount, target }: { correctCount: number; target: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {getCorrectCountDotStates(correctCount, target).map((filled, i) => (
        <span key={i} className={`mr-1 inline-block h-3 w-3 rounded-full ${filled ? 'bg-emerald-500' : 'bg-gray-200'}`} />
      ))}
      <span className="ml-2 text-sm text-muted-foreground">
        {correctCount === -1 ? '—' : `${correctCount}/${target}`}
      </span>
    </div>
  );
}

const buildColumns = (bet: ExoticBet): DataTableColumn<StraightExoticRow>[] => [
  {
    key: 'date',
    header: 'Date',
    priority: true,
    render: (row) => <span className="font-medium tabular-nums">{parseRaceId(row.race_id).date || '—'}</span>,
  },
  {
    key: 'race',
    header: 'Race',
    priority: true,
    render: (row) => (
      <span className="font-mono text-sm text-muted-foreground">
        {parseRaceId(row.race_id).identifier || row.race_id}
      </span>
    ),
  },
  {
    key: 'result',
    header: 'Result',
    priority: true,
    render: (row) => <MatchBadge correctCount={row.correct_count} target={row.pick_count} />,
  },
  {
    key: 'correct',
    header: 'Correct',
    priority: false,
    render: (row) => <CorrectCountDots correctCount={row.correct_count} target={row.pick_count} />,
  },
  {
    key: 'cost',
    header: 'Boxed cost (100%)',
    priority: false,
    render: (row) => {
      const combinations = straightCombinations(bet, row.horses.length);
      return (
        <span className="tabular-nums">
          {formatOutlay(combinations)}
          <span className="ml-1 text-xs text-muted-foreground">({combinations} combos)</span>
        </span>
      );
    },
  },
  {
    key: 'horses',
    header: 'Horse IDs',
    priority: false,
    render: (row) => (
      <span className="font-mono text-xs text-muted-foreground">{row.horses?.join(', ') ?? '—'}</span>
    ),
  },
];

export function ExoticRacesTable({ rows, bet }: { rows: StraightExoticRow[]; bet: ExoticBet }) {
  return (
    <DataTable
      columns={buildColumns(bet)}
      data={rows}
      getRowKey={(row) => row.race_id}
      emptyMessage="No matching races found for this date range."
    />
  );
}
